/**
 * Kidify store locator (sections/kidify-store-locator.liquid).
 *
 * Reads the stores the section printed as JSON, then draws:
 *   - store cards: photos, MRT badge in its line colour, Open now / Closed
 *     (Singapore time, public holidays from the section setting), games and
 *     services, and Directions / Citymapper / WhatsApp / Email;
 *   - a Leaflet map on OneMap tiles with a pin per store;
 *   - search (name, area, postal code, MRT, game) and game chips;
 *   - "Near me": the browser's location, kept in the page only, sorts the
 *     stores by distance and adds a "you are here" dot;
 *   - on a phone, a Map / List pill that swaps the two views.
 * With data-compact it draws the cards only (the pickup store on the track
 * order page), and "Show on map" links to data-map-url.
 * Card and pin stay in step: picking one highlights the other.
 */

(() => {
  const MRT_COLOURS = {
    NS: '#d42e12', EW: '#009645', CG: '#009645', NE: '#9900aa', CC: '#fa9e0d', CE: '#fa9e0d',
    DT: '#005ec4', TE: '#9d5b25', CR: '#97c616', CP: '#97c616', JS: '#0099aa', JE: '#0099aa', JW: '#0099aa',
    BP: '#748477', SE: '#748477', SW: '#748477', PE: '#748477', PW: '#748477',
  };
  const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const DAY_NAMES = { sun: 'Sunday', mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday' };

  const esc = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  /* ---------- Opening hours, Singapore time ---------- */

  function sgNow() {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Singapore', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23', weekday: 'short',
      })
        .formatToParts(new Date())
        .map((p) => [p.type, p.value])
    );
    const date = `${parts.year}-${parts.month}-${parts.day}`;
    return { date, minutes: Number(parts.hour) * 60 + Number(parts.minute), day: parts.weekday.slice(0, 3).toLowerCase() };
  }

  const toMinutes = (hhmm) => {
    const [h, m] = String(hhmm).split(':').map(Number);
    return h * 60 + (m || 0);
  };

  const label = (hhmm) => {
    const [h, m] = String(hhmm).split(':').map(Number);
    const suffix = h >= 12 ? 'pm' : 'am';
    const hour = h % 12 || 12;
    return m ? `${hour}:${String(m).padStart(2, '0')}${suffix}` : `${hour}${suffix}`;
  };

  function addDays(dateString, days) {
    const d = new Date(`${dateString}T12:00:00+08:00`);
    d.setUTCDate(d.getUTCDate() + days);
    return { date: d.toISOString().slice(0, 10), day: DAYS[d.getUTCDay()] };
  }

  function rangesFor(hours, date, day, holidays) {
    if (!hours) return null;
    if (holidays.has(date) && hours.ph) return hours.ph;
    return hours[day] || [];
  }

  function openStatus(hours, holidays) {
    if (!hours || typeof hours !== 'object') return null;
    const now = sgNow();
    const today = rangesFor(hours, now.date, now.day, holidays);
    for (const [from, to] of today) {
      if (now.minutes >= toMinutes(from) && now.minutes < toMinutes(to)) {
        const soon = toMinutes(to) - now.minutes <= 45;
        return { open: true, text: soon ? `Closing soon · ${label(to)}` : `Open now · closes ${label(to)}` };
      }
    }
    const later = today.find(([from]) => toMinutes(from) > now.minutes);
    if (later) return { open: false, text: `Closed · opens ${label(later[0])}` };
    for (let i = 1; i <= 7; i++) {
      const next = addDays(now.date, i);
      const ranges = rangesFor(hours, next.date, next.day, holidays);
      if (ranges.length) {
        const when = i === 1 ? 'tomorrow' : DAY_NAMES[next.day];
        return { open: false, text: `Closed · opens ${label(ranges[0][0])} ${when}` };
      }
    }
    return { open: false, text: 'Closed' };
  }

  /* ---------- Distance ---------- */

  function km(a, b) {
    const R = 6371;
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
  }

  const distanceLabel = (d) => (d < 1 ? `${Math.round(d * 1000 / 50) * 50} m away` : `${d.toFixed(1)} km away`);

  /* ---------- Element ---------- */

  class KidifyStoreLocator extends HTMLElement {
    connectedCallback() {
      try {
        this.stores = JSON.parse(this.querySelector('[data-stores]').textContent).filter((s) => s.lat && s.lng);
        this.config = JSON.parse(this.querySelector('[data-config]').textContent || '{}');
      } catch {
        this.stores = [];
        this.config = {};
      }
      this.holidays = new Set(String(this.config.holidays || '').split(/[\s,]+/).filter(Boolean));
      this.game = '';
      this.query = '';
      this.me = null;
      this.activeId = null;

      this.cardsEl = this.querySelector('[data-cards]');
      this.countEl = this.querySelector('[data-count]');
      this.emptyEl = this.querySelector('[data-empty]');
      this.body = this.querySelector('.kidify-loc__body');

      // Compact: cards only, no search, chips or map (the pickup store on
      // the track order page). "Show on map" links to the Find a store page.
      this.compact = this.hasAttribute('data-compact');
      if (this.compact) {
        this.#renderCards();
        this.timer = setInterval(() => this.#refreshStatus(), 60000);
        return;
      }

      this.#renderChips();
      this.#renderCards();
      this.#bind();
      this.#whenLeaflet(() => this.#initMap());
      // Open / closed changes with the clock.
      this.timer = setInterval(() => this.#refreshStatus(), 60000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    #whenLeaflet(fn, tries = 0) {
      if (window.L) return fn();
      if (tries > 100) return;
      setTimeout(() => this.#whenLeaflet(fn, tries + 1), 100);
    }

    #visible() {
      // "pokemon" finds "Pokémon": accents are ignored on both sides.
      const fold = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const q = fold(this.query.trim());
      let list = this.stores.filter((s) => {
        if (this.game && !(s.games || []).includes(this.game)) return false;
        if (!q) return true;
        const hay = [s.name, s.area, s.postal, s.address, s.mrtStation, s.mrtCode, ...(s.games || []), ...(s.services || [])]
          .join(' ');
        const folded = fold(hay);
        return q.split(/\s+/).every((word) => folded.includes(word));
      });
      if (this.me) list = list.map((s) => ({ ...s, distance: km(this.me, s) })).sort((a, b) => a.distance - b.distance);
      return list;
    }

    #renderChips() {
      const games = [...new Set(this.stores.flatMap((s) => s.games || []))];
      const chips = this.querySelector('[data-chips]');
      chips.innerHTML = ['', ...games]
        .map(
          (g) =>
            `<button type="button" class="kidify-loc__chip" data-game="${esc(g)}" aria-pressed="${g === this.game}">${esc(g || 'All stores')}</button>`
        )
        .join('');
      chips.hidden = games.length === 0;
    }

    #card(s) {
      const status = openStatus(s.hours, this.holidays);
      const line = (s.mrtCode || '').replace(/\d.*/, '').toUpperCase();
      const mrt = s.mrtStation
        ? `<span class="kidify-loc__mrt">${
            s.mrtCode ? `<span class="kidify-loc__mrt-code" style="--mrt:${MRT_COLOURS[line] || '#748477'}">${esc(s.mrtCode)}</span>` : ''
          }${esc(s.mrtStation)} MRT${s.mrtDistance ? `, ${esc(s.mrtDistance)}` : ''}</span>`
        : '';
      const dir = s.directions || `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`;
      const actions = [
        `<a class="kidify-lbtn kidify-lbtn--primary" href="${esc(dir)}" target="_blank" rel="noopener">Directions</a>`,
        s.type === 'Kidify store' && this.config.citymapper
          ? `<a class="kidify-lbtn kidify-lbtn--ghost" href="${esc(this.config.citymapper)}" target="_blank" rel="noopener">Citymapper</a>`
          : '',
        s.whatsapp
          ? `<a class="kidify-lbtn kidify-lbtn--ghost" href="https://wa.me/${esc(String(s.whatsapp).replace(/\D/g, ''))}" target="_blank" rel="noopener">WhatsApp</a>`
          : '',
        // Encoded address: assets/kidify-email.js makes it a mailto link
        // on the visitor's first real tap, click or key press.
        s.emailCode ? `<a class="kidify-lbtn kidify-lbtn--ghost" href="#kidify-email:${esc(s.emailCode)}">Email</a>` : '',
        s.website ? `<a class="kidify-lbtn kidify-lbtn--ghost" href="${esc(s.website)}" target="_blank" rel="noopener">Website</a>` : '',
      ].join('');
      return `<li>
        <article class="kidify-loc__card${s.id === this.activeId ? ' is-active' : ''}" data-store="${esc(s.id)}" tabindex="-1" aria-labelledby="loc-${esc(s.id)}">
          ${
            s.photos?.length
              ? `<div class="kidify-loc__photos">${s.photos
                  .map((src, i) => `<img src="${esc(src)}" alt="${i === 0 ? esc(`${s.name} storefront`) : ''}" loading="lazy" width="900" height="506">`)
                  .join('')}</div>`
              : ''
          }
          <div class="kidify-loc__info">
            <span class="kidify-loc__type">${esc(s.type)}</span>
            <h2 class="kidify-loc__name" id="loc-${esc(s.id)}">${esc(s.name)}</h2>
            ${s.tagline ? `<p class="kidify-loc__tagline">${esc(s.tagline)}</p>` : ''}
            <div class="kidify-loc__meta">
              ${status ? `<span class="kidify-loc__status kidify-loc__status--${status.open ? 'open' : 'closed'}" data-status>${esc(status.text)}</span>` : ''}
              ${mrt}
              ${s.distance != null ? `<span class="kidify-loc__distance">${esc(distanceLabel(s.distance))}</span>` : ''}
            </div>
            ${s.address ? `<p class="kidify-loc__address">${esc(s.address)}</p>` : ''}
            ${s.hoursText ? `<details class="kidify-loc__hours"><summary>Opening hours</summary><p>${esc(s.hoursText)}</p></details>` : ''}
            ${
              (s.games || []).length || (s.services || []).length
                ? `<ul class="kidify-loc__tags" role="list" aria-label="What you'll find">${(s.games || [])
                    .map((g) => `<li class="kidify-loc__tag">${esc(g)}</li>`)
                    .join('')}${(s.services || []).map((g) => `<li class="kidify-loc__tag kidify-loc__tag--service">${esc(g)}</li>`).join('')}</ul>`
                : ''
            }
            <div class="kidify-loc__actions">
              ${
                this.compact
                  ? this.dataset.mapUrl
                    ? `<a class="kidify-lbtn kidify-lbtn--ghost" href="${esc(this.dataset.mapUrl)}">Show on map</a>`
                    : ''
                  : `<button type="button" class="kidify-lbtn kidify-lbtn--ghost" data-show="${esc(s.id)}">Show on map</button>`
              }
              ${actions}
            </div>
          </div>
        </article>
      </li>`;
    }

    #renderCards() {
      const list = this.#visible();
      this.cardsEl.innerHTML = list.map((s) => this.#card(s)).join('');
      if (this.countEl) this.countEl.textContent = `${list.length} ${list.length === 1 ? 'store' : 'stores'}${this.me ? ', nearest first' : ''}`;
      if (this.emptyEl) this.emptyEl.hidden = list.length > 0;
      this.#syncMarkers(list);
    }

    #refreshStatus() {
      for (const el of this.querySelectorAll('[data-store]')) {
        const s = this.stores.find((x) => x.id === el.dataset.store);
        const badge = el.querySelector('[data-status]');
        const status = s && openStatus(s.hours, this.holidays);
        if (badge && status) {
          badge.textContent = status.text;
          badge.className = `kidify-loc__status kidify-loc__status--${status.open ? 'open' : 'closed'}`;
        }
      }
    }

    #bind() {
      this.querySelector('[data-search]').addEventListener('input', (e) => {
        this.query = e.target.value;
        this.#renderCards();
      });
      this.querySelector('[data-chips]').addEventListener('click', (e) => {
        const chip = e.target.closest('[data-game]');
        if (!chip) return;
        this.game = chip.dataset.game;
        for (const c of this.querySelectorAll('[data-game]')) c.setAttribute('aria-pressed', String(c === chip));
        this.#renderCards();
      });
      this.cardsEl.addEventListener('click', (e) => {
        const show = e.target.closest('[data-show]');
        if (!show) return;
        this.#select(show.dataset.show, { fly: true, openMap: true });
      });
      this.querySelector('[data-near]').addEventListener('click', () => this.#nearMe());
      this.querySelector('[data-toggle]').addEventListener('click', () => this.#setView(this.body.dataset.view === 'map' ? 'list' : 'map'));
    }

    #setView(view) {
      this.body.dataset.view = view;
      const toggle = this.querySelector('[data-toggle]');
      toggle.setAttribute('aria-pressed', String(view === 'map'));
      toggle.querySelector('[data-toggle-map]').hidden = view === 'map';
      toggle.querySelector('[data-toggle-list]').hidden = view !== 'map';
      if (view === 'map' && this.map) setTimeout(() => this.map.invalidateSize(), 50);
      window.scrollTo({ top: Math.max(0, this.body.getBoundingClientRect().top + window.scrollY - 140), behavior: 'instant' });
    }

    #isPhone() {
      return window.matchMedia('(max-width: 899px)').matches;
    }

    #select(id, { fly = false, openMap = false } = {}) {
      this.activeId = id;
      for (const el of this.querySelectorAll('[data-store]')) el.classList.toggle('is-active', el.dataset.store === id);
      for (const [mid, marker] of this.markers || []) marker.getElement()?.querySelector('.kidify-loc__pin')?.classList.toggle('is-active', mid === id);
      const store = this.stores.find((s) => s.id === id);
      if (openMap && this.#isPhone()) this.#setView('map');
      if (store && this.map && fly) {
        this.map.flyTo([store.lat, store.lng], Math.max(this.map.getZoom(), 16), { duration: 0.6 });
        this.markers.get(id)?.openPopup();
      }
    }

    /* ---------- Map ---------- */

    #initMap() {
      const el = this.querySelector('[data-map]');
      if (!el || this.map) return;
      const L = window.L;
      this.map = L.map(el, {
        zoomControl: true,
        minZoom: 11,
        maxZoom: 19,
        maxBounds: [[1.144, 103.535], [1.494, 104.1]],
        maxBoundsViscosity: 1,
        scrollWheelZoom: false,
      });
      L.tileLayer('https://www.onemap.gov.sg/maps/tiles/Default/{z}/{x}/{y}.png', {
        detectRetina: true,
        maxZoom: 19,
        minZoom: 11,
        attribution:
          '<img src="https://www.onemap.gov.sg/web-assets/images/logo/om_logo.png" style="height:16px;width:16px" alt="">&nbsp;<a href="https://www.onemap.gov.sg/" target="_blank" rel="noopener">OneMap</a>&nbsp;&copy;&nbsp;contributors&nbsp;&#124;&nbsp;<a href="https://www.sla.gov.sg/" target="_blank" rel="noopener">Singapore Land Authority</a>',
      }).addTo(this.map);
      // Scroll the page past the map; zoom with the buttons, a pinch, or a
      // wheel once the map has been clicked.
      this.map.on('click', () => this.map.scrollWheelZoom.enable());
      this.map.on('mouseout', () => this.map.scrollWheelZoom.disable());
      this.markers = new Map();
      this.#syncMarkers(this.#visible(), true);
    }

    #syncMarkers(list, fit = false) {
      if (!this.map) return;
      const L = window.L;
      for (const marker of this.markers.values()) marker.remove();
      this.markers.clear();
      for (const s of list) {
        const icon = L.divIcon({
          className: '',
          html: `<div class="kidify-loc__pin${s.type === 'Partner store' ? ' kidify-loc__pin--partner' : ''}${
            s.id === this.activeId ? ' is-active' : ''
          }"><span>K</span></div>`,
          iconSize: [44, 44],
          iconAnchor: [6, 44],
          popupAnchor: [16, -44],
        });
        const status = openStatus(s.hours, this.holidays);
        const marker = L.marker([s.lat, s.lng], { icon, title: s.name, keyboard: true })
          .bindPopup(
            `<span class="kidify-loc__pop-name">${esc(s.name)}</span>${status ? esc(status.text) : ''}${
              s.mrtStation ? `<br>${esc(s.mrtStation)} MRT${s.mrtDistance ? `, ${esc(s.mrtDistance)}` : ''}` : ''
            }`
          )
          .addTo(this.map);
        marker.on('click', () => {
          this.#select(s.id);
          if (!this.#isPhone()) this.querySelector(`[data-store="${CSS.escape(s.id)}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
        this.markers.set(s.id, marker);
      }
      if (fit || this.me) this.#fit(list);
    }

    #fit(list) {
      const points = list.map((s) => [s.lat, s.lng]);
      if (this.me) points.push([this.me.lat, this.me.lng]);
      if (!points.length) return this.map.setView([1.3521, 103.8198], 11);
      if (points.length === 1) return this.map.setView(points[0], 15);
      this.map.fitBounds(points, { padding: [48, 48], maxZoom: 15 });
    }

    #nearMe() {
      const button = this.querySelector('[data-near]');
      if (!navigator.geolocation) return (this.countEl.textContent = 'Location is not available in this browser.');
      button.setAttribute('aria-busy', 'true');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          button.removeAttribute('aria-busy');
          this.me = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          if (this.map) {
            this.meMarker?.remove();
            this.meMarker = window.L.marker([this.me.lat, this.me.lng], {
              icon: window.L.divIcon({ className: '', html: '<div class="kidify-loc__me"></div>', iconSize: [18, 18], iconAnchor: [9, 9] }),
              title: 'You are here',
              keyboard: false,
            }).addTo(this.map);
          }
          this.#renderCards();
        },
        () => {
          button.removeAttribute('aria-busy');
          this.countEl.textContent = 'We could not get your location. You can still search by area or MRT.';
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }
  }

  if (!customElements.get('kidify-store-locator')) customElements.define('kidify-store-locator', KidifyStoreLocator);
})();
